import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Select, Table, Tabs, message } from "antd";
import { CloudUploadOutlined, SearchOutlined } from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { memberState } from "../../../../state/atoms";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitImportPreviewDto,
  IMPORT_RESOLUTION_CREATE,
  ImportAmbiguousRowDto,
  ImportMappingDto,
  ImportParseDto,
  ImportRowDto,
} from "../../../../domain/DataKitDto";
import { env } from "../../../../env/env";
import { hasDataKitBeta } from "./dataKitAccess";
import {
  FIRST_PAIR,
  MAPPING_SECTIONS,
  labelForTarget,
  needsPairIndex,
  splitSuggestedTarget,
  validateMapping,
} from "./dataKitImportMapping";
import "./DataKitImportPage.scss";

const MAX_PAIRS = 6;

/** How a decision row was resolved. Absent means "not decided yet". */
type Decision = "add" | "skip" | string;

const pathOf = (row: ImportRowDto) =>
  row.groupPath && row.groupPath.length > 0 ? row.groupPath : [row.groupName];

const groupLabel = (row: ImportRowDto) => pathOf(row).join(" › ");

/** Decision sets are keyed by (name, group path) — one line per set, as the design asks. */
const setKeyOf = (row: ImportRowDto) =>
  `${(row.name || "").trim().toLowerCase()}\u001f${pathOf(row)
    .map((p) => p.trim().toLowerCase())
    .join("\u001f")}`;

const priceOf = (row: ImportRowDto) =>
  (row.prices || []).map((p) => p.price).filter(Boolean).join(" / ") || "––";

/**
 * The spreadsheet importer, as its own page under the kit.
 *
 * It was a modal whose review step paged through every row 8 at a time, with the controls moving
 * as you paged, and whose only answer to "these rows share a name" was to skip them silently. The
 * three steps are the same three Timber's importer has — upload, match, review — and the review
 * step is tabbed rather than paginated so a decision is never more than one click away and nothing
 * is hidden behind a page number.
 *
 * Import stays all-or-nothing: nothing is written until the last button, and the server recomputes
 * its own plan rather than trusting this one (a kit can change between a preview and an apply).
 */
export function DataKitImportPage() {
  const { kitId } = useParams();
  const navigate = useNavigate();
  const member = useRecoilValue(memberState);
  const canWrite = !!member && (member.owner || member.role === "Admin");

  const [kit, setKit] = useState<DataKitDto | null>(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [parsed, setParsed] = useState<ImportParseDto | null>(null);
  const [mapping, setMapping] = useState<ImportMappingDto[]>([]);
  const [preview, setPreview] = useState<DataKitImportPreviewDto | null>(null);
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [skippedNew, setSkippedNew] = useState<Record<number, boolean>>({});
  const [tab, setTab] = useState("decide");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const kitPath = `${env.dashboardPath}/account/data-kits/${kitId}`;

  useEffect(() => {
    if (!kitId) return;
    DataKitApi.getKit(kitId)
      .then((res) => setKit(res.data))
      .catch(() => message.error("Could not load this data kit"));
  }, [kitId]);

  const errors = useMemo(() => validateMapping(mapping), [mapping]);

  const takeFile = (next: File | null) => {
    if (!next) return;
    if (!/\.(xlsx|xls|csv)$/i.test(next.name)) {
      message.error("Choose a .xlsx, .xls or .csv file");
      return;
    }
    setFile(next);
  };

  const parse = () => {
    if (!file || !kitId) return;
    setBusy(true);
    DataKitApi.parseImport(kitId, file)
      .then((res) => {
        const data = res.data;
        setParsed(data);
        setMapping(
          data.headers.map((_, columnIndex) => {
            const suggestion = data.suggestedMapping.find((s) => s.columnIndex === columnIndex);
            const { target, pairIndex } = splitSuggestedTarget(suggestion?.target || "");
            return { columnIndex, target, pairIndex };
          })
        );
        setStep(2);
      })
      .catch(() => message.error("Could not read that file"))
      .finally(() => setBusy(false));
  };

  const runPreview = () => {
    if (!parsed || !kitId) return;
    setBusy(true);
    DataKitApi.previewImport(kitId, {
      headers: parsed.headers,
      rows: parsed.rows,
      rowNumbers: parsed.rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions: {},
    })
      .then((res) => {
        const data = res.data;
        setPreview(data);
        // Every decision row defaults to "add as separate items" — the answer that is right for
        // the shape this almost always is (one row per vintage), and the one the design names.
        const defaults: Record<number, Decision> = {};
        (data.ambiguous || []).forEach((a) => {
          if (a.canCreateSeparately) defaults[a.row.rowNumber] = "add";
        });
        setDecisions(defaults);
        setSkippedNew({});
        setTab((data.ambiguous || []).length > 0 ? "decide" : "new");
        setStep(3);
      })
      .catch(() => message.error("Could not preview this import"))
      .finally(() => setBusy(false));
  };

  const setTarget = (columnIndex: number, target: string) => {
    setMapping((current) =>
      current.map((m) =>
        m.columnIndex === columnIndex
          ? { ...m, target, pairIndex: needsPairIndex(target) ? m.pairIndex ?? FIRST_PAIR : null }
          : m
      )
    );
  };

  const setPair = (columnIndex: number, pairIndex: number) => {
    setMapping((current) =>
      current.map((m) => (m.columnIndex === columnIndex ? { ...m, pairIndex } : m))
    );
  };

  /** Slots already spoken for, so the dropdown can say so rather than letting two columns collide. */
  const usedSlots = useMemo(() => {
    const used = new Set<string>();
    mapping.forEach((m) => {
      if (m.target === "IGNORE") return;
      used.add(needsPairIndex(m.target) ? `${m.target}:${m.pairIndex ?? FIRST_PAIR}` : m.target);
    });
    return used;
  }, [mapping]);

  const decisionSets = useMemo(() => {
    if (!preview) return [];
    const bySet = new Map<string, ImportAmbiguousRowDto[]>();
    (preview.ambiguous || []).forEach((a) => {
      const key = setKeyOf(a.row);
      bySet.set(key, [...(bySet.get(key) || []), a]);
    });
    return Array.from(bySet.entries()).map(([key, rows]) => ({ key, rows }));
  }, [preview]);

  const unresolvedCount = useMemo(
    () =>
      (preview?.ambiguous || []).filter((a) => {
        const d = decisions[a.row.rowNumber];
        return d !== "add" && d !== "skip" && !a.candidateItemIds.includes(d as string);
      }).length,
    [preview, decisions]
  );

  const newRows = useMemo(() => preview?.creates || [], [preview]);
  const updateRows = useMemo(() => preview?.updates || [], [preview]);
  const upToDateRows = useMemo(() => preview?.alreadyUpToDate || [], [preview]);

  const filterRows = useCallback(
    (rows: ImportRowDto[]) => {
      const q = search.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          groupLabel(r).toLowerCase().includes(q)
      );
    },
    [search]
  );

  /** Every group the file will file something into, with how many rows land in each. */
  const groupSummary = useMemo(() => {
    const counts = new Map<string, number>();
    [...newRows, ...updateRows, ...upToDateRows].forEach((r) => {
      const label = groupLabel(r);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    (preview?.ambiguous || []).forEach((a) => {
      if (decisions[a.row.rowNumber] === "skip") return;
      const label = groupLabel(a.row);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([group, rows]) => ({ group, rows }))
      .sort((a, b) => a.group.localeCompare(b.group));
  }, [newRows, updateRows, upToDateRows, preview, decisions]);

  /** What the Import button will actually write, updating live as rows are skipped. */
  const importCount = useMemo(() => {
    const creates = newRows.filter((r) => !skippedNew[r.rowNumber]).length;
    const decided = (preview?.ambiguous || []).filter((a) => {
      const d = decisions[a.row.rowNumber];
      return d === "add" || a.candidateItemIds.includes(d as string);
    }).length;
    return creates + updateRows.length + decided;
  }, [newRows, skippedNew, updateRows, preview, decisions]);

  const runImport = () => {
    if (!parsed || !kitId) return;
    const resolutions: Record<number, string> = {};
    (preview?.ambiguous || []).forEach((a) => {
      const d = decisions[a.row.rowNumber];
      if (d === "add") resolutions[a.row.rowNumber] = IMPORT_RESOLUTION_CREATE;
      else if (d && d !== "skip") resolutions[a.row.rowNumber] = d as string;
    });
    // A skipped New row is dropped from the payload outright: there is no "skip" resolution for a
    // row that was never ambiguous, and the server plans from whatever rows it is handed.
    const skippedRowNumbers = new Set(
      Object.entries(skippedNew).filter(([, v]) => v).map(([k]) => Number(k))
    );
    const rows = parsed.rows.filter(
      (_, i) => !skippedRowNumbers.has(parsed.rowNumbers[i])
    );
    const rowNumbers = parsed.rowNumbers.filter((n) => !skippedRowNumbers.has(n));

    setBusy(true);
    DataKitApi.applyImport(kitId, {
      headers: parsed.headers,
      rows,
      rowNumbers,
      mapping: mapping.filter((m) => m.target !== "IGNORE"),
      fileName: parsed.fileName,
      resolutions,
    })
      .then((res) => {
        const r = res.data;
        message.success(`Imported ${r.created} new and ${r.updated} updated`);
        navigate(kitPath);
      })
      .catch(() => message.error("Could not import this file"))
      .finally(() => setBusy(false));
  };

  if (!hasDataKitBeta(member) || !canWrite) return null;

  const rowColumns = [
    { title: "Row", dataIndex: "rowNumber", key: "rowNumber", width: 70 },
    { title: "Item", dataIndex: "name", key: "name" },
    {
      title: "Group",
      key: "group",
      width: 220,
      render: (_: unknown, row: ImportRowDto) => groupLabel(row),
    },
    {
      title: "Price",
      key: "price",
      width: 130,
      render: (_: unknown, row: ImportRowDto) => priceOf(row),
    },
  ];

  return (
    <div className="data-kit-import-page">
      <nav className="data-kit-breadcrumb">
        <Link to={`${env.dashboardPath}/account/data-kits`}>Data Kits</Link>
        <span className="data-kit-breadcrumb-sep">&rsaquo;</span>
        <Link to={kitPath}>{kit?.name || "Kit"}</Link>
        <span className="data-kit-breadcrumb-sep">&rsaquo;</span>
        <span className="data-kit-breadcrumb-current">Import</span>
      </nav>

      <header className="import-header">
        <h1>Import</h1>
        <ol className="import-steps">
          <li className={step === 1 ? "on" : step > 1 ? "done" : ""}>1. Upload</li>
          <li className={step === 2 ? "on" : step > 2 ? "done" : ""}>2. Match columns</li>
          <li className={step === 3 ? "on" : ""}>3. Review</li>
        </ol>
      </header>

      {step === 1 && (
        <section className="import-step">
          <p className="import-hint">
            A spreadsheet with one row per item. Start from the template if you don&apos;t have one.
          </p>
          <a className="import-template-link" href={`${env.dashboardPath}/data-kit-import-template.csv`} download>
            Download the template
          </a>

          <div
            className={`import-drop${dragging ? " dragging" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              takeFile(e.dataTransfer.files?.[0] || null);
            }}
            onClick={() => fileInput.current?.click()}
          >
            <CloudUploadOutlined className="import-drop-icon" />
            <p className="import-drop-title">Drop your spreadsheet here</p>
            <p className="import-drop-sub">or click to choose a .xlsx, .xls or .csv file</p>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={(e) => takeFile(e.target.files?.[0] || null)}
            />
          </div>

          {!!file && (
            <div className="import-file-card">
              <span className="import-file-name">{file.name}</span>
              <span className="import-file-meta">
                {parsed
                  ? `${parsed.rows.length} rows · ${parsed.headers.length} columns`
                  : `${Math.max(1, Math.round(file.size / 1024))} KB`}
              </span>
              <button type="button" className="import-file-remove" onClick={() => setFile(null)}>
                Remove
              </button>
            </div>
          )}
        </section>
      )}

      {step === 2 && !!parsed && (
        <section className="import-step">
          <p className="import-hint">
            Tell us what each column holds. Group and Item Name are required; anything left as
            &quot;Don&apos;t import&quot; is ignored.
          </p>
          <Table
            className="import-map-table"
            dataSource={mapping}
            rowKey="columnIndex"
            pagination={false}
            size="small"
            columns={[
              {
                title: "Column",
                key: "column",
                render: (_: unknown, row: ImportMappingDto) => (
                  <div className="import-col-cell">
                    <div className="import-col-header">{parsed.headers[row.columnIndex] || "—"}</div>
                    <div className="import-col-sample">
                      {(parsed.rows[0] || [])[row.columnIndex] || ""}
                    </div>
                  </div>
                ),
              },
              {
                title: "Import as",
                key: "target",
                width: 260,
                render: (_: unknown, row: ImportMappingDto) => (
                  <Select
                    className="import-select"
                    value={row.target}
                    onChange={(value) => setTarget(row.columnIndex, value)}
                    options={MAPPING_SECTIONS.map((section) => ({
                      label: section.label,
                      options: section.targets.map((t) => {
                        const slot = needsPairIndex(t.value)
                          ? `${t.value}:${row.pairIndex ?? FIRST_PAIR}`
                          : t.value;
                        const taken = t.value !== "IGNORE" && t.value !== row.target && usedSlots.has(slot);
                        return {
                          value: t.value,
                          disabled: taken && !needsPairIndex(t.value),
                          label: taken && !needsPairIndex(t.value) ? `${t.label} — already mapped` : t.label,
                        };
                      }),
                    }))}
                  />
                ),
              },
              {
                title: "Slot",
                key: "pair",
                width: 100,
                render: (_: unknown, row: ImportMappingDto) =>
                  needsPairIndex(row.target) ? (
                    <Select
                      className="import-pair"
                      value={row.pairIndex ?? FIRST_PAIR}
                      onChange={(value) => setPair(row.columnIndex, value)}
                      options={Array.from({ length: MAX_PAIRS }, (_, i) => {
                        const slot = i + FIRST_PAIR;
                        const key = `${row.target}:${slot}`;
                        const taken = slot !== (row.pairIndex ?? FIRST_PAIR) && usedSlots.has(key);
                        return {
                          value: slot,
                          disabled: taken,
                          label: taken
                            ? `${labelForTarget(row.target)} ${slot} — already mapped`
                            : `${labelForTarget(row.target)} ${slot}`,
                        };
                      })}
                    />
                  ) : null,
              },
            ]}
          />
          {errors.length > 0 && (
            <Alert
              className="import-errors"
              type="warning"
              message={errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            />
          )}
        </section>
      )}

      {step === 3 && !!preview && (
        <section className="import-step">
          <Input
            className="import-search"
            size="large"
            placeholder="Search this file"
            prefix={<SearchOutlined style={{ color: "#999" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />

          <Tabs
            className="import-review-tabs"
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: "decide",
                label: `Needs a decision • ${decisionSets.length}`,
                children: (
                  <div className="import-decisions">
                    {decisionSets.length === 0 && (
                      <p className="import-empty">Nothing to decide — every row matched cleanly.</p>
                    )}
                    {decisionSets.length > 0 && (
                      <div className="import-decide-all">
                        <Button
                          size="small"
                          onClick={() =>
                            setDecisions(
                              Object.fromEntries(
                                (preview.ambiguous || [])
                                  .filter((a) => a.canCreateSeparately)
                                  .map((a) => [a.row.rowNumber, "add"])
                              )
                            )
                          }
                        >
                          Add all as separate items
                        </Button>
                        <Button
                          size="small"
                          onClick={() =>
                            setDecisions(
                              Object.fromEntries(
                                (preview.ambiguous || []).map((a) => [a.row.rowNumber, "skip"])
                              )
                            )
                          }
                        >
                          Skip all
                        </Button>
                      </div>
                    )}
                    {decisionSets.map(({ key, rows }) => (
                      <div className="import-decision-set" key={key}>
                        <div className="import-decision-head">
                          <span className="import-decision-name">{rows[0].row.name}</span>
                          <span className="import-decision-group">{groupLabel(rows[0].row)}</span>
                          <span className="import-decision-count">
                            {rows.length} row{rows.length === 1 ? "" : "s"}
                          </span>
                          {rows[0].canCreateSeparately && (
                            <div className="import-decision-actions">
                              <Button
                                size="small"
                                onClick={() =>
                                  setDecisions((c) => ({
                                    ...c,
                                    ...Object.fromEntries(rows.map((r) => [r.row.rowNumber, "add"])),
                                  }))
                                }
                              >
                                Add as separate items
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  setDecisions((c) => ({
                                    ...c,
                                    ...Object.fromEntries(rows.map((r) => [r.row.rowNumber, "skip"])),
                                  }))
                                }
                              >
                                Skip this set
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="import-decision-reason">{rows[0].reason}</p>
                        <ul className="import-decision-rows">
                          {rows.map((a) => {
                            const d = decisions[a.row.rowNumber];
                            return (
                              <li key={a.row.rowNumber}>
                                <span className="import-decision-row-num">Row {a.row.rowNumber}</span>
                                <span className="import-decision-row-price">{priceOf(a.row)}</span>
                                {a.candidateItemIds.length > 0 ? (
                                  <Select
                                    className="import-decision-pick"
                                    size="small"
                                    placeholder="Choose what this row is"
                                    value={d === "skip" ? undefined : (d as string)}
                                    onChange={(value) =>
                                      setDecisions((c) => ({ ...c, [a.row.rowNumber]: value }))
                                    }
                                    options={a.candidateItemIds.map((id) => ({
                                      value: id,
                                      label: `Update existing item ${id.slice(0, 8)}`,
                                    }))}
                                  />
                                ) : (
                                  <span className={`import-decision-state ${d === "skip" ? "skip" : "add"}`}>
                                    {d === "skip" ? "Skipped" : "Add as a separate item"}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="import-decision-toggle"
                                  onClick={() =>
                                    setDecisions((c) => ({
                                      ...c,
                                      [a.row.rowNumber]: d === "skip" ? "add" : "skip",
                                    }))
                                  }
                                >
                                  {d === "skip" ? "Add" : "Skip"}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: "new",
                label: `New • ${newRows.length}`,
                children: (
                  <Table
                    className="import-review-table"
                    dataSource={filterRows(newRows)}
                    rowKey="rowNumber"
                    pagination={false}
                    size="small"
                    scroll={{ y: 420 }}
                    rowClassName={(row: ImportRowDto) =>
                      skippedNew[row.rowNumber] ? "import-row-skipped" : ""
                    }
                    columns={[
                      ...rowColumns,
                      {
                        title: "",
                        key: "skip",
                        width: 90,
                        render: (_: unknown, row: ImportRowDto) => (
                          <button
                            type="button"
                            className="import-decision-toggle"
                            onClick={() =>
                              setSkippedNew((c) => ({ ...c, [row.rowNumber]: !c[row.rowNumber] }))
                            }
                          >
                            {skippedNew[row.rowNumber] ? "Add" : "Skip"}
                          </button>
                        ),
                      },
                    ]}
                    locale={{ emptyText: "Nothing new in this file" }}
                  />
                ),
              },
              {
                key: "existing",
                label: `Already in kit • ${updateRows.length + upToDateRows.length}`,
                children: (
                  <>
                    <Table
                      className="import-review-table"
                      dataSource={filterRows(updateRows)}
                      rowKey="rowNumber"
                      pagination={false}
                      size="small"
                      scroll={{ y: 300 }}
                      columns={rowColumns}
                      locale={{ emptyText: "No changes to existing items" }}
                    />
                    {upToDateRows.length > 0 && (
                      <p className="import-uptodate">
                        <strong>{upToDateRows.length}</strong> row
                        {upToDateRows.length === 1 ? " is" : "s are"} already up to date and
                        won&apos;t be imported again.
                      </p>
                    )}
                    {(preview.mergedDuplicates || []).length > 0 && (
                      <p className="import-uptodate">
                        <strong>{(preview.mergedDuplicates || []).length}</strong> exact duplicate
                        row{(preview.mergedDuplicates || []).length === 1 ? "" : "s"} merged.
                      </p>
                    )}
                  </>
                ),
              },
              {
                key: "groups",
                label: `Groups • ${groupSummary.length}`,
                children: (
                  <Table
                    className="import-review-table"
                    dataSource={groupSummary}
                    rowKey="group"
                    pagination={false}
                    size="small"
                    scroll={{ y: 420 }}
                    columns={[
                      { title: "Group", dataIndex: "group", key: "group" },
                      { title: "Items", dataIndex: "rows", key: "rows", width: 100, align: "center" as const },
                    ]}
                    locale={{ emptyText: "No groups in this file" }}
                  />
                ),
              },
            ]}
          />

          {preview.skipped.length > 0 && (
            <p className="import-uptodate">
              <strong>{preview.skipped.length}</strong> row
              {preview.skipped.length === 1 ? "" : "s"} skipped for having no name.
            </p>
          )}
        </section>
      )}

      <footer className="import-footer">
        {step === 3 && unresolvedCount > 0 && (
          <span className="import-unresolved">
            {unresolvedCount} row{unresolvedCount === 1 ? "" : "s"} still need a decision
          </span>
        )}
        <span className="import-footer-spacer" />
        <Button size="large" onClick={() => navigate(kitPath)} disabled={busy}>
          Cancel
        </Button>
        {step > 1 && (
          <Button size="large" onClick={() => setStep(step - 1)} disabled={busy}>
            Back
          </Button>
        )}
        {step === 1 && (
          <Button size="large" className="primary" onClick={parse} loading={busy} disabled={!file}>
            Next
          </Button>
        )}
        {step === 2 && (
          <Button
            size="large"
            className="primary"
            onClick={runPreview}
            loading={busy}
            disabled={errors.length > 0}
          >
            Next
          </Button>
        )}
        {step === 3 && (
          <Button
            size="large"
            className="primary"
            onClick={runImport}
            loading={busy}
            disabled={importCount === 0}
          >
            Import {importCount} item{importCount === 1 ? "" : "s"}
          </Button>
        )}
      </footer>
    </div>
  );
}
